import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// User interaction tracking types
export interface FormState {
  formId: string;
  fieldValues: Record<string, any>;
  timestamp: number;
  isComplete: boolean;
  validationErrors?: Record<string, string>;
}

export interface ModalState {
  modalId: string;
  isOpen: boolean;
  data?: any;
  timestamp: number;
}

export interface InteractionState {
  type: 'click' | 'scroll' | 'input' | 'navigation' | 'modal' | 'image' | 'camera';
  target: string;
  data?: any;
  timestamp: number;
  screen: string;
}

export interface MediaState {
  type: 'image' | 'video' | 'audio';
  id: string;
  filename?: string;
  dataUrl?: string;
  metadata?: Record<string, any>;
  timestamp: number;
  screen: string;
}

export interface UserSessionState {
  sessionId: string;
  startTime: number;
  lastActivity: number;
  
  // Navigation state
  currentScreen: string;
  screenHistory: string[];
  screenStartTime: number;
  
  // Form states
  forms: Record<string, FormState>;
  
  // Modal states
  modals: Record<string, ModalState>;
  
  // User interactions
  interactions: InteractionState[];
  
  // Media (images, photos, etc.)
  mediaItems: Record<string, MediaState>;
  
  // Draft data for incomplete forms/flows
  drafts: Record<string, any>;
  
  // Accessibility preferences
  accessibility: {
    lastFocusedElement?: string;
    scrollPosition?: Record<string, number>;
    fontSize?: number;
    highContrast?: boolean;
    reducedMotion?: boolean;
  };
  
  // Session management
  settings: {
    autoSave: boolean;
    saveInterval: number; // in milliseconds
    maxInteractionHistory: number;
  };
}

const initialState: UserSessionState = {
  sessionId: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  startTime: Date.now(),
  lastActivity: Date.now(),
  
  currentScreen: '',
  screenHistory: [],
  screenStartTime: Date.now(),
  
  forms: {},
  modals: {},
  interactions: [],
  mediaItems: {},
  drafts: {},
  
  accessibility: {},
  
  settings: {
    autoSave: true,
    saveInterval: 30000, // 30 seconds
    maxInteractionHistory: 100, // Keep last 100 interactions
  },
};

const userStateSlice = createSlice({
  name: 'userState',
  initialState,
  reducers: {
    // Session management
    updateLastActivity: (state) => {
      state.lastActivity = Date.now();
    },
    
    initializeSession: (state, action: PayloadAction<string>) => {
      state.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      state.startTime = Date.now();
      state.lastActivity = Date.now();
      state.currentScreen = action.payload;
      state.screenHistory = [action.payload];
      state.screenStartTime = Date.now();
    },
    
    // Screen/Navigation tracking
    trackScreenChange: (state, action: PayloadAction<string>) => {
      const newScreen = action.payload;
      if (state.currentScreen !== newScreen) {
        state.currentScreen = newScreen;
        state.screenHistory.push(newScreen);
        state.screenStartTime = Date.now();
        state.lastActivity = Date.now();
        
        // Keep screen history manageable
        if (state.screenHistory && state.screenHistory.length > 50) {
          state.screenHistory = state.screenHistory.slice(-50);
        }
      }
    },
    
    // Form state management
    updateFormState: (state, action: PayloadAction<{
      formId: string;
      fieldName: string;
      value: any;
      isComplete?: boolean;
      validationErrors?: Record<string, string>;
    }>) => {
      const { formId, fieldName, value, isComplete = false, validationErrors } = action.payload;
      
      if (!state.forms[formId]) {
        state.forms[formId] = {
          formId,
          fieldValues: {},
          timestamp: Date.now(),
          isComplete: false,
        };
      }
      
      state.forms[formId].fieldValues[fieldName] = value;
      state.forms[formId].timestamp = Date.now();
      state.forms[formId].isComplete = isComplete;
      
      if (validationErrors) {
        state.forms[formId].validationErrors = validationErrors;
      }
      
      state.lastActivity = Date.now();
    },
    
    clearFormState: (state, action: PayloadAction<string>) => {
      delete state.forms[action.payload];
    },
    
    // Modal state tracking
    trackModalState: (state, action: PayloadAction<{
      modalId: string;
      isOpen: boolean;
      data?: any;
    }>) => {
      const { modalId, isOpen, data } = action.payload;
      
      state.modals[modalId] = {
        modalId,
        isOpen,
        data,
        timestamp: Date.now(),
      };
      
      state.lastActivity = Date.now();
    },
    
    // User interaction tracking
    trackInteraction: (state, action: PayloadAction<{
      type: InteractionState['type'];
      target: string;
      data?: any;
    }>) => {
      const { type, target, data } = action.payload;
      
      const interaction: InteractionState = {
        type,
        target,
        data,
        timestamp: Date.now(),
        screen: state.currentScreen,
      };
      
      if (!state.interactions) {
        state.interactions = [];
      }
      state.interactions.push(interaction);
      
      // Keep interaction history manageable
      if (state.interactions && state.interactions.length > state.settings.maxInteractionHistory) {
        state.interactions = state.interactions.slice(-state.settings.maxInteractionHistory);
      }
      
      state.lastActivity = Date.now();
    },
    
    // Media state management
    saveMediaItem: (state, action: PayloadAction<{
      type: 'image' | 'video' | 'audio';
      id: string;
      filename?: string;
      dataUrl?: string;
      metadata?: Record<string, any>;
    }>) => {
      const { type, id, filename, dataUrl, metadata } = action.payload;
      
      state.mediaItems[id] = {
        type,
        id,
        filename,
        dataUrl,
        metadata,
        timestamp: Date.now(),
        screen: state.currentScreen,
      };
      
      state.lastActivity = Date.now();
    },
    
    removeMediaItem: (state, action: PayloadAction<string>) => {
      delete state.mediaItems[action.payload];
    },
    
    // Draft data management
    saveDraft: (state, action: PayloadAction<{
      key: string;
      data: any;
    }>) => {
      const { key, data } = action.payload;
      state.drafts[key] = data;
      state.lastActivity = Date.now();
    },
    
    removeDraft: (state, action: PayloadAction<string>) => {
      delete state.drafts[action.payload];
    },
    
    // Accessibility state
    updateAccessibilityState: (state, action: PayloadAction<Partial<UserSessionState['accessibility']>>) => {
      state.accessibility = {
        ...state.accessibility,
        ...action.payload,
      };
      state.lastActivity = Date.now();
    },
    
    // Save scroll position
    saveScrollPosition: (state, action: PayloadAction<{
      screen: string;
      position: number;
    }>) => {
      const { screen, position } = action.payload;
      if (!state.accessibility.scrollPosition) {
        state.accessibility.scrollPosition = {};
      }
      state.accessibility.scrollPosition[screen] = position;
    },
    
    // Settings management
    updateSettings: (state, action: PayloadAction<Partial<UserSessionState['settings']>>) => {
      state.settings = {
        ...state.settings,
        ...action.payload,
      };
    },
    
    // Complete session cleanup
    clearSession: (state) => {
      // Reset to initial state but preserve settings
      const currentSettings = state.settings;
      Object.assign(state, initialState);
      state.settings = currentSettings;
      state.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    },
    
    // Partial cleanup (for logout but keeping some data)
    resetUserData: (state) => {
      // Clear sensitive user data but keep accessibility preferences
      state.forms = {};
      state.drafts = {};
      state.interactions = [];
      state.mediaItems = {};
      state.modals = {};
      state.screenHistory = [state.currentScreen];
      state.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      state.startTime = Date.now();
      state.lastActivity = Date.now();
    },
  },
});

export const {
  updateLastActivity,
  initializeSession,
  trackScreenChange,
  updateFormState,
  clearFormState,
  trackModalState,
  trackInteraction,
  saveMediaItem,
  removeMediaItem,
  saveDraft,
  removeDraft,
  updateAccessibilityState,
  saveScrollPosition,
  updateSettings,
  clearSession,
  resetUserData,
} = userStateSlice.actions;

export default userStateSlice.reducer;

// Selectors
export const selectCurrentSession = (state: { userState: UserSessionState }) => state.userState;
export const selectFormState = (formId: string) => (state: { userState: UserSessionState }) => state.userState.forms[formId];
export const selectModalState = (modalId: string) => (state: { userState: UserSessionState }) => state.userState.modals[modalId];
export const selectMediaItems = (state: { userState: UserSessionState }) => state.userState.mediaItems;
export const selectDraft = (key: string) => (state: { userState: UserSessionState }) => state.userState.drafts[key];
export const selectAccessibilityState = (state: { userState: UserSessionState }) => state.userState.accessibility;
export const selectLastInteractions = (count: number = 10) => (state: { userState: UserSessionState }) =>
  state.userState.interactions ? state.userState.interactions.slice(-count) : [];